import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../components/LanguageProvider';
import { usePageSetup } from '../hooks/usePageSetup';
import { getSpecificationEntries } from '../profileQuote';
import { getPublicErrorMessage } from '../publicError';
import { supabaseClient } from '../supabase';

const title = 'My Profile - INGAZ Print';

function quoteReference(id) {
  return `#${String(id || '').slice(0, 8).toUpperCase()}`;
}

function statusClassName(status) {
  return String(status || 'pending').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function Profile() {
  const { logout, openLogin, user } = useAuth();
  const { language, t } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [quotesError, setQuotesError] = useState('');
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  usePageSetup({ title });

  useEffect(() => {
    if (!user?.email) {
      setQuotes([]);
      setQuotesError('');
      return undefined;
    }

    let active = true;
    setQuotesLoading(true);
    setQuotesError('');

    supabaseClient
      .from('quote_requests')
      .select('id, product_type, quantity, specifications, notes, status, created_at')
      .eq('customer_email', user.email)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (!active) return;
        setQuotes(data || []);
        setQuotesError(error ? getPublicErrorMessage(error, {
          network: t('Please check your connection and try again.', 'يرجى التحقق من الاتصال والمحاولة مرة أخرى.'),
        }, t('Your quotes could not be loaded. Please try again.', 'تعذر تحميل طلباتك. يرجى المحاولة مرة أخرى.')) : '');
        setQuotesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [t, user?.email]);

  useEffect(() => {
    if (!selectedQuote) return undefined;

    const oldOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setSelectedQuote(null);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = oldOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [selectedQuote]);

  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(
    language === 'ar' ? 'ar-EG' : 'en-GB',
    { dateStyle: 'medium' },
  ), [language]);

  const formatDate = (date) => {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? t('Date unavailable', 'التاريخ غير متاح') : dateFormatter.format(parsed);
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || t('Customer', 'العميل');
  const phone = user?.phone || user?.user_metadata?.phone;
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  const pendingQuotes = quotes.filter((quote) => String(quote.status).toLowerCase() === 'pending').length;
  const selectedSpecifications = selectedQuote ? getSpecificationEntries(selectedQuote.specifications) : [];
  const selectedSourcePath = selectedQuote?.specifications && typeof selectedQuote.specifications === 'object'
    ? selectedQuote.specifications.source_path
    : null;

  return (
    <>
      <SiteHeader />
      <main className="profile-page">
        <section className="profile-section">
          {user ? (
            <>
              <section className="profile-header">
                <div className="profile-identity">
                  <div className="profile-avatar" aria-hidden="true">{initials}</div>
                  <div>
                    <span className="profile-eyebrow">{t('Customer account', 'حساب العميل')}</span>
                    <h1>{displayName}</h1>
                    <div className="profile-contact-list">
                      <span>{user.email}</span>
                      <span>{phone || t('No phone number added', 'لم تتم إضافة رقم هاتف')}</span>
                    </div>
                  </div>
                </div>
                <div className="profile-overview">
                  <div className="profile-stat">
                    <strong>{quotes.length}</strong>
                    <span>{t('Total quotes', 'إجمالي الطلبات')}</span>
                  </div>
                  <div className="profile-stat">
                    <strong>{pendingQuotes}</strong>
                    <span>{t('Pending', 'قيد الانتظار')}</span>
                  </div>
                  <button className="btn3 profile-logout" onClick={logout} type="button">
                    {t('Logout', 'تسجيل الخروج')}
                  </button>
                </div>
              </section>

              <section className="profile-quotes">
                <div className="profile-quotes-heading">
                  <div>
                    <span className="profile-eyebrow">{t('Quote history', 'سجل الطلبات')}</span>
                    <h2>{t('Past Quote Requests', 'طلبات عروض الأسعار السابقة')}</h2>
                    <p>{t('Select a request to see its complete details.', 'اختر طلباً لعرض جميع تفاصيله.')}</p>
                  </div>
                  <Link className="btn2" to="/#products">
                    {t('Request a New Quote', 'اطلب عرض سعر جديد')}
                  </Link>
                </div>

                {quotesLoading ? (
                  <div className="profile-empty"><p>{t('Loading quotes...', 'جاري تحميل الطلبات...')}</p></div>
                ) : quotesError ? (
                  <div className="profile-empty profile-error">
                    <h3>{t('Quotes could not be loaded', 'تعذر تحميل الطلبات')}</h3>
                    <p>{quotesError}</p>
                  </div>
                ) : quotes.length > 0 ? (
                  <div className="profile-quote-grid">
                    {quotes.map((quote) => {
                      const previewSpecifications = getSpecificationEntries(quote.specifications).slice(0, 2);

                      return (
                        <button
                          className="profile-quote-card"
                          key={quote.id}
                          onClick={() => setSelectedQuote(quote)}
                          type="button"
                        >
                          <div className="profile-quote-heading">
                            <span className="profile-quote-reference">{quoteReference(quote.id)}</span>
                            <span className={`profile-status profile-status-${statusClassName(quote.status)}`}>
                              {quote.status || t('Pending', 'قيد الانتظار')}
                            </span>
                          </div>
                          <h3>{quote.product_type}</h3>
                          <div className="profile-quote-meta">
                            <span>
                              <small>{t('Quantity', 'الكمية')}</small>
                              <strong>{quote.quantity}</strong>
                            </span>
                            <span>
                              <small>{t('Requested', 'تاريخ الطلب')}</small>
                              <strong>{formatDate(quote.created_at)}</strong>
                            </span>
                          </div>
                          {previewSpecifications.length > 0 && (
                            <div className="profile-quote-preview">
                              {previewSpecifications.map(([key, value]) => (
                                <span key={key}>{key}: {value}</span>
                              ))}
                            </div>
                          )}
                          <span className="profile-quote-view">
                            {t('View full details', 'عرض جميع التفاصيل')}
                            <span aria-hidden="true">→</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="profile-empty">
                    <h3>{t('No quote requests yet', 'لا توجد طلبات عروض أسعار بعد')}</h3>
                    <p>{t("You haven't created any quotes yet.", 'لم تقم بإنشاء أي طلبات عروض أسعار بعد.')}</p>
                    <Link className="btn2" to="/#products">
                      {t('Get a Quote', 'احصل على عرض سعر')}
                    </Link>
                  </div>
                )}
              </section>
            </>
          ) : (
            <div className="profile-empty">
              <h1>{t('Please Login', 'يرجى تسجيل الدخول')}</h1>
              <p>
                {t(
                  'You need to be logged in to view your profile and past quotes.',
                  'يجب عليك تسجيل الدخول لعرض ملفك الشخصي وطلبات عروض الأسعار السابقة.',
                )}
              </p>
              <button className="btn2" onClick={openLogin} type="button">{t('Go to Login', 'انتقل إلى تسجيل الدخول')}</button>
            </div>
          )}
        </section>
      </main>

      {selectedQuote && (
        <div
          className="profile-quote-modal"
          onClick={(event) => event.target === event.currentTarget && setSelectedQuote(null)}
          role="presentation"
        >
          <section aria-labelledby="quoteDetailsTitle" aria-modal="true" className="profile-quote-modal-content" role="dialog">
            <button
              aria-label={t('Close quote details', 'إغلاق تفاصيل الطلب')}
              className="profile-quote-modal-close"
              onClick={() => setSelectedQuote(null)}
              type="button"
            >
              &times;
            </button>
            <div className="profile-quote-modal-header">
              <div>
                <span className="profile-quote-reference">{quoteReference(selectedQuote.id)}</span>
                <h2 id="quoteDetailsTitle">{selectedQuote.product_type}</h2>
                <p>{t('Requested on', 'تم الطلب في')} {formatDate(selectedQuote.created_at)}</p>
              </div>
              <span className={`profile-status profile-status-${statusClassName(selectedQuote.status)}`}>
                {selectedQuote.status || t('Pending', 'قيد الانتظار')}
              </span>
            </div>

            <div className="profile-quote-detail-summary">
              <div>
                <span>{t('Product', 'المنتج')}</span>
                <strong>{selectedQuote.product_type}</strong>
              </div>
              <div>
                <span>{t('Quantity', 'الكمية')}</span>
                <strong>{selectedQuote.quantity}</strong>
              </div>
              <div>
                <span>{t('Status', 'الحالة')}</span>
                <strong>{selectedQuote.status || t('Pending', 'قيد الانتظار')}</strong>
              </div>
            </div>

            <div className="profile-quote-detail-section">
              <h3>{t('Request Specifications', 'مواصفات الطلب')}</h3>
              {selectedSpecifications.length > 0 ? (
                <dl className="profile-specification-list">
                  {selectedSpecifications.map(([key, value]) => (
                    <div key={key}>
                      <dt>{key}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="profile-detail-muted">{t('No specifications were saved for this request.', 'لم يتم حفظ مواصفات لهذا الطلب.')}</p>
              )}
            </div>

            <div className="profile-quote-detail-section">
              <h3>{t('Additional Notes', 'ملاحظات إضافية')}</h3>
              <p className={selectedQuote.notes ? 'profile-quote-notes' : 'profile-detail-muted'}>
                {selectedQuote.notes || t('No additional notes were added.', 'لم تتم إضافة ملاحظات أخرى.')}
              </p>
            </div>

            <div className="profile-quote-modal-actions">
              {selectedSourcePath?.startsWith('/') && (
                <Link className="btn2" onClick={() => setSelectedQuote(null)} to={`${selectedSourcePath}#configuration`}>
                  {t('Request Similar Quote', 'اطلب عرض سعر مشابه')}
                </Link>
              )}
              <button className="btn3" onClick={() => setSelectedQuote(null)} type="button">
                {t('Close', 'إغلاق')}
              </button>
            </div>
          </section>
        </div>
      )}
      <SiteFooter />
    </>
  );
}

export default Profile;
